import styled from "styled-components";

import { formatAdminDateRange } from "@pages/AdminDashboard/formatAdminDate";
import { InvoiceLink } from "@pages/AdminDashboard/InvoiceLink";
import { OpenInButtons } from "@pages/AdminDashboard/OpenInButtons";
import {
  caseStatusLabel,
  orderStatusLabel,
  shippingMethodLabel,
} from "@pages/AdminDashboard/plainLanguageLabels";
import { telehealthCaseUrl } from "@pages/AdminDashboard/telehealthLinks";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { CopyButton } from "@pages/AdminDashboard/ui/CopyButton";
import { DataTable, DataTableColumn, EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import { ExternalLink, shortenIdentifier } from "@pages/AdminDashboard/ui/DataTable/cells";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { Case, CaseOrderSummary, Trip } from "@types-runway/case";

import {
  describeOrderShipsTo,
  listCasesNewestFirst,
  listOrdersNewestFirst,
  titleCasesAndOrders,
} from "./accountCases";
import { OrderTracking } from "./OrderTracking";

const { color, typeScale } = adminTokens;
const [, spaceSm, spaceMd, spaceLg] = adminTokens.spacing;
const caseHeaderGap = 18;
const identityMinWidth = 150;
const noValue = "—";
const noTripDate = "No trip date";
const hint = "Redo cancels or replaces an Order · Ship changes method or address";

const Hint = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const CaseList = styled.div`
  display: flex;
  flex-direction: column;
`;

// One Case with its Orders, set apart from the one above by a hairline (ACCT-R10…R12).
const CaseBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceMd}px;
  padding: ${spaceLg}px 0;

  & + & {
    border-top: 1px solid ${color.border};
  }
`;

const CaseHeader = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: ${({ $isPhone }) => ($isPhone ? "column" : "row")};
  align-items: ${({ $isPhone }) => ($isPhone ? "stretch" : "center")};
  flex-wrap: wrap;
  gap: ${({ $isPhone }) => ($isPhone ? spaceSm : caseHeaderGap)}px;
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: ${identityMinWidth}px;
`;

const IdRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
`;

const Strong = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
  overflow-wrap: anywhere;
`;

const Soft = styled.span`
  color: ${color.inkSoft};
`;

const Meta = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const Spacer = styled.div`
  flex: 1;
`;

// CVIEW-R4: the destination, a dot and the dates of the trip.
const describeTrip = (trip?: Trip & { destination?: string }) => {
  if (!trip?.from) return noTripDate;

  const dates = formatAdminDateRange(trip.from, trip.to);

  return trip.destination ? `${trip.destination} · ${dates}` : dates;
};

// ACCT-R12: the Order's ID by its first segment with the whole ID on hover, its copy Button and
// its number below; on the account the ID is text, as the Open Question on ACCT-R12 defaults.
const OrderCell = ({ order }: { order: CaseOrderSummary }) => (
  <Identity>
    <IdRow>
      <Strong title={order.order_id}>{shortenIdentifier(order.order_id)}</Strong>
      <CopyButton value={order.order_id} what="order ID" />
    </IdRow>
    <Meta>{`#${order.id}`}</Meta>
  </Identity>
);

// The same shares for every Case, so the Orders of all Cases line up (ACCT-R29).
const orderColumns = (caseRecord: Case): DataTableColumn<CaseOrderSummary>[] => [
  { id: "order", header: "Order", width: "21%", render: (order) => <OrderCell order={order} /> },
  {
    id: "status",
    header: "Order status",
    width: "18%",
    phonePlacement: "badge",
    render: ({ status }) => <StatusBadge status={status} label={orderStatusLabel(status)} />,
  },
  {
    id: "method",
    header: "Shipping method",
    width: "15%",
    render: ({ shipping_method: method }) => (method ? shippingMethodLabel(method) : noValue),
  },
  {
    id: "tracking",
    header: "Tracking",
    width: "23%",
    render: (order) => <OrderTracking order={order} />,
  },
  {
    id: "shipsTo",
    header: "Ships to",
    width: "23%",
    render: () => <Soft>{describeOrderShipsTo(caseRecord.shippingAddress)}</Soft>,
  },
];

const CaseIdentity = ({ caseRecord }: { caseRecord: Case }) => {
  const number = String(caseRecord.id ?? "");
  const { caseId } = caseRecord;

  return (
    <Identity>
      <IdRow>
        <Strong>{`Case #${number}`}</Strong>
        <CopyButton value={number} what="Case ID" />
      </IdRow>
      {caseId ? (
        <ExternalLink href={telehealthCaseUrl(caseId)} title={caseId}>
          {shortenIdentifier(caseId)}
        </ExternalLink>
      ) : (
        <Meta>{noValue}</Meta>
      )}
    </Identity>
  );
};

// A Case with its number, Case ID, payment, trip, Redo and Ship, its status only when it has no
// Order, and its Orders below (ACCT-R10…R13).
const AccountCase = ({ caseRecord }: { caseRecord: Case }) => {
  const isPhone = useIsPhoneLayout();
  const label = `Case #${caseRecord.id}`;
  const orders = listOrdersNewestFirst(caseRecord.orders ?? []);
  const status = caseRecord.case_status;

  return (
    <CaseBlock role="group" aria-label={label}>
      <CaseHeader $isPhone={isPhone}>
        <CaseIdentity caseRecord={caseRecord} />
        {orders.length === 0 && status && (
          <span>
            <StatusBadge status={status} label={caseStatusLabel(status)} />
          </span>
        )}
        <InvoiceLink invoiceId={caseRecord.invoiceId}>
          {caseRecord.paid ? "✓ Paid" : "Unpaid"}
        </InvoiceLink>
        <Soft>{describeTrip(caseRecord.trip)}</Soft>
        {!isPhone && <Spacer />}
        <OpenInButtons caseId={caseRecord.caseId} subject={label} />
      </CaseHeader>
      {orders.length === 0 ? (
        <Soft>No Order yet for this Case.</Soft>
      ) : (
        <DataTable
          label={`Orders for ${label}`}
          columns={orderColumns(caseRecord)}
          rows={orders}
          getRowKey={(order) => String(order.id)}
          emptyState={{ title: "No Order yet for this Case." }}
        />
      )}
    </CaseBlock>
  );
};

interface CasesAndOrdersProps {
  cases: Case[];
}

// The account's "Cases and Orders" card: every Case, newest first, each with its Orders
// (ACCT-R9…R14, CVIEW-R1, CVIEW-R6, USER-R20).
export const CasesAndOrders = ({ cases }: CasesAndOrdersProps) => (
  <div data-testid="user-cases">
    <Card title={titleCasesAndOrders(cases)} titleLevel={3} aside={<Hint>{hint}</Hint>}>
      {cases.length === 0 ? (
        <EmptyStateCard title="No cases on file." />
      ) : (
        <CaseList>
          {listCasesNewestFirst(cases).map((caseRecord) => (
            <AccountCase key={caseRecord.id} caseRecord={caseRecord} />
          ))}
        </CaseList>
      )}
    </Card>
  </div>
);
